import { type ChangeEvent, useCallback } from 'react';
import type { Lens } from '@hookform/lenses';
import { useFieldArray } from '@hookform/lenses/rhf';
import { useFormContext, useWatch } from 'react-hook-form';
import { z } from 'zod/v4';
import { Grid } from '@interxfi/design-system/components/alignment/grid';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { Button } from '@interxfi/design-system/components/button/button';
import { PlainIconButton } from '@interxfi/design-system/components/button/plain-icon/plain-icon-button';
import { DEFAULT_HEADING_LEVEL_3_TYPOGRAPHY, Typography } from '@interxfi/design-system/components/typography';
import DeleteIcon from '@interxfi/design-system/icons/react/icon-delete';
import PlusIcon from '@interxfi/design-system/icons/react/icon-plus';
import type { ResponsiveValue } from '@interxfi/design-system/util/responsive';
import type { IxcRegistrationV1AffiliationsAssociatedPersons, IxcRegistrationV1Name } from '@interxfi/types/generated/api';
import { isDeepEqual } from '@interxfi/utilities/equality/deep';
import { AppCheckboxInputWithLens } from '../../../../../../components/form/app-checkbox-input';
import { AppSwitchInputWithLens } from '../../../../../../components/form/app-switch-input';
import { AppTextInputWithLens } from '../../../../../../components/form/app-text-input';
import {
  defaultNameFormValues,
  formValuesToRegistrationName,
  getInitialFormValuesForRegistrationName,
  NameForm,
  type NameFormValues,
  nameFormSchema,
} from '../../../../shared-form/name-form';

export const associatedPersonFormSchema = z.object({
  isHouseholdMemberOrIndependent: z.boolean(),
  name: nameFormSchema.optional(),
  employer: z.string().nonempty({ error: 'Employer must be provided' }),
  complianceOfficer: z.string().nonempty({ error: 'Compliance Officer must be provided' }),
  complianceOfficerEmail: z
    .email('Compliance Officer Email must be a valid email address')
    .nonempty({ error: 'Compliance Office Email must be provided' }),
});

export type AssociatedPersonFormValues = z.infer<typeof associatedPersonFormSchema>;

export const defaultAssociatedPersonFormValues: AssociatedPersonFormValues = {
  isHouseholdMemberOrIndependent: false,
  employer: '',
  complianceOfficer: '',
  complianceOfficerEmail: '',
};

export const associatedPersonsFormSchema = z.object({
  isProviding: z.boolean(),
  value: z.array(associatedPersonFormSchema),
});

export type AssociatedPersonsFormValues = z.infer<typeof associatedPersonsFormSchema>;

export const defaultAssociatedPersonsFormValues: AssociatedPersonsFormValues = {
  isProviding: false,
  value: [],
};

export function formValuesToAssociatedPerson(
  values: AssociatedPersonFormValues,
  profileName: NameFormValues | undefined,
): IxcRegistrationV1AffiliationsAssociatedPersons | undefined {
  const nameToUse = values.isHouseholdMemberOrIndependent ? values.name : profileName;
  if (!nameToUse) {
    return undefined;
  }

  const name = formValuesToRegistrationName(nameToUse);

  if (!name) {
    return undefined;
  }

  return {
    name,
    employer: values.employer,
    complianceOfficer: values.complianceOfficer,
    complianceOfficerEmail: values.complianceOfficerEmail,
  };
}

export function formValuesToAssociatedPersons(
  values: AssociatedPersonsFormValues,
  profileName: NameFormValues | undefined,
): IxcRegistrationV1AffiliationsAssociatedPersons[] | undefined {
  if (!values.value || !values.isProviding) {
    return undefined;
  }

  const mapped = values.value.reduce<IxcRegistrationV1AffiliationsAssociatedPersons[]>((acc, curr) => {
    const associatedPerson = formValuesToAssociatedPerson(curr, profileName);

    if (associatedPerson) {
      acc.push(associatedPerson);
    }

    return acc;
  }, []);

  return mapped.length > 0 ? mapped : undefined;
}

export function getInitialFormValuesForAssociatedPerson(
  associatedPerson: IxcRegistrationV1AffiliationsAssociatedPersons | undefined,
  profileName: IxcRegistrationV1Name | undefined,
): AssociatedPersonFormValues | undefined {
  if (!associatedPerson) {
    return undefined;
  }

  const nameMatchesParentProfile = !associatedPerson.name || !profileName || isDeepEqual(associatedPerson.name, profileName);

  return {
    ...defaultAssociatedPersonFormValues,
    isHouseholdMemberOrIndependent: !nameMatchesParentProfile,
    name: getInitialFormValuesForRegistrationName(nameMatchesParentProfile ? undefined : associatedPerson.name),
    employer: associatedPerson.employer || '',
    complianceOfficer: associatedPerson.complianceOfficer || '',
    complianceOfficerEmail: associatedPerson.complianceOfficerEmail || '',
  };
}

export function getInitialFormValuesForAssociatedPersons(
  persons: IxcRegistrationV1AffiliationsAssociatedPersons[] | undefined,
  profileName: IxcRegistrationV1Name | undefined,
): AssociatedPersonsFormValues | undefined {
  if (!persons?.length) {
    return undefined;
  }

  return {
    ...defaultAssociatedPersonsFormValues,
    isProviding: true,
    value: persons.reduce<AssociatedPersonFormValues[]>((acc, curr) => {
      const associatedPerson = getInitialFormValuesForAssociatedPerson(curr, profileName);
      if (associatedPerson) {
        acc.push(associatedPerson);
      }

      return acc;
    }, []),
  };
}

interface AssociatedPersonFormProps {
  lens: Lens<AssociatedPersonFormValues>;
}

const complianceOfficerGrid: ResponsiveValue<number> = {
  sm: 1,
  md: 2,
};

function AssociatedPersonForm({ lens }: AssociatedPersonFormProps) {
  const { setValue } = useFormContext();
  const isHouseholdMemberOrIndependentLens = lens.focus('isHouseholdMemberOrIndependent');
  const requiresName = useWatch(isHouseholdMemberOrIndependentLens.interop());
  const nameLens = lens.focus('name');
  const { name: nameFieldName } = nameLens.interop();
  const handleHouseholdMemberToggle = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setValue(nameFieldName, e.target.checked ? { ...defaultNameFormValues } : undefined, { shouldValidate: true });
    },
    [nameFieldName, setValue],
  );

  return (
    <VerticalStack gap={7}>
      <AppSwitchInputWithLens
        label="This is a household member or independent"
        labelSide="right"
        lens={isHouseholdMemberOrIndependentLens}
        onChange={handleHouseholdMemberToggle}
      />

      <VerticalStack gap={4}>
        {requiresName ? <NameForm lens={lens.focus('name').defined()} /> : null}

        <AppTextInputWithLens label="Employer" lens={lens.focus('employer')} required />

        <Grid autoColumns={complianceOfficerGrid}>
          <AppTextInputWithLens label="Compliance Officer" lens={lens.focus('complianceOfficer')} required />
          <AppTextInputWithLens label="Compliance Officer Email" lens={lens.focus('complianceOfficerEmail')} required />
        </Grid>
      </VerticalStack>
    </VerticalStack>
  );
}

interface AssociatedPersonsFormProps {
  lens: Lens<AssociatedPersonsFormValues>;
}

export function AssociatedPersonsForm({ lens }: AssociatedPersonsFormProps) {
  const { setValue } = useFormContext();
  const valueLens = lens.focus('value');
  const valueLensInterop = valueLens.interop();
  const { name: valueName } = valueLensInterop;
  const { append, fields, remove } = useFieldArray(valueLensInterop);
  const hasMultiple = fields.length > 1;
  const isProvidingLens = lens.focus('isProviding');
  const isProviding = useWatch(isProvidingLens.interop());
  const handleAssociatedPersonToggle = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setValue(valueName, e.target.checked ? [{ ...defaultAssociatedPersonFormValues }] : [], { shouldValidate: true });
    },
    [setValue, valueName],
  );

  return (
    <VerticalStack gap={7}>
      <AppCheckboxInputWithLens
        label={
          <Typography lineHeight="1.125rem" size="0.875rem">
            This individual, household member, or independent is an{' '}
            <Typography lineHeight="1.125rem" size="0.875rem" weight="bold">
              Associated Person
            </Typography>
            , meaning they work for a broker-dealer, exchange, or FINRA.
          </Typography>
        }
        lens={isProvidingLens}
        onChange={handleAssociatedPersonToggle}
      />

      {isProviding ? (
        <VerticalStack gap={7}>
          {valueLens.map(fields, (arrayField, l, i) => (
            <VerticalStack gap={7} key={arrayField.id}>
              {hasMultiple ? (
                <HorizontalStack blockAlign="center" gap={4} wrap="nowrap">
                  <PlainIconButton
                    aria-label={`Remove associated person #${i + 1}`}
                    onClick={() => {
                      remove(i);
                    }}
                    tone="danger-dk"
                  >
                    <DeleteIcon aria-hidden height={16} />
                  </PlainIconButton>

                  <Typography {...DEFAULT_HEADING_LEVEL_3_TYPOGRAPHY}>Associated Person</Typography>
                </HorizontalStack>
              ) : null}

              <AssociatedPersonForm key={arrayField.id} lens={l} />
            </VerticalStack>
          ))}

          <Button
            affixIcon={<PlusIcon aria-hidden />}
            onClick={() => {
              append({ ...defaultAssociatedPersonFormValues });
            }}
            size="medium"
            variant="light"
          >
            Add an Associated Person
          </Button>
        </VerticalStack>
      ) : null}
    </VerticalStack>
  );
}
